import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
    MdRefresh,
    MdShowChart,
    MdSavings,
    MdCalendarMonth,
    MdTrendingUp,
    MdPercent,
    MdRule,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const PLAN_STYLES = {
    lite: { accent: "#94a3b8", label: "Lite" },
    plus: { accent: "#3b82f6", label: "Plus" },
    pro: { accent: "#8b5cf6", label: "Pro" },
    max: { accent: "#f59e0b", label: "Max" },
};

const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const parseChartPayload = (data) => {
    if (Array.isArray(data)) {
        return { downline: [...data], selfPlans: [] };
    }
    if (data && typeof data === "object") {
        const downline = Array.isArray(data.downline) ? [...data.downline] : [];
        const selfPlans = Array.isArray(data.self) ? [...data.self] : [];
        downline.sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
        return { downline, selfPlans };
    }
    return { downline: [], selfPlans: [] };
};

const formatAmount = (value) => {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    if (isNaN(n)) return "—";
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`;
};

const formatRecoveryMultiplier = (value) => {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    if (isNaN(n)) return "—";
    return `${n}x`;
};

const formatCommission = (value) => {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    if (isNaN(n)) return "—";
    return `${n}%`;
};

const directRecoveryPhrase = (count) => {
    const n = Number(count);
    if (isNaN(n) || n <= 0) return "";
    const label = n === 1 ? "direct recovery trade" : "direct recovery trades";
    return `${n} ${label}`;
};

const investmentPhrase = (value) => {
    const n = Number(value);
    if (isNaN(n) || n <= 0) return "";
    return `${formatAmount(n)} investment`;
};

/** Human-readable qualification text for a downline level */
const formatDownlineConditions = (row) => {
    const direct = directRecoveryPhrase(row.direct_recovery_count);
    const investment = investmentPhrase(row.self_recovery_value);
    const condition = row.condition;

    if (!direct && !investment) return "No requirements specified";

    if (direct && !investment) {
        return `Required ${direct}`;
    }

    if (!direct && investment) {
        return `Required ${investment}`;
    }

    if (condition === "OR") {
        return `Required ${direct} and/or ${investment}`;
    }

    if (condition === "AND") {
        return `Required ${direct} and ${investment}`;
    }

    return `Required ${direct}, ${investment}`;
};

const PlanBadge = ({ plan }) => {
    const key = (plan || "").toLowerCase();
    const cfg = PLAN_STYLES[key] || { accent: "#059669", label: plan || "—" };
    return (
        <span
            className="badge rounded-pill text-uppercase"
            style={{
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                backgroundColor: `${cfg.accent}22`,
                color: cfg.accent,
                border: `1px solid ${cfg.accent}55`,
            }}
        >
            {cfg.label}
        </span>
    );
};

const RecoveryTradeCommissionChart = ({ theme = "dark" }) => {
    const [downline, setDownline] = useState([]);
    const [selfPlans, setSelfPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const tableStyles = useMemo(
        () => ({
            card: styles.card,
            headerBg: isDark ? "#0f172a" : "#f1f5f9",
            downlineHeaderColor: isDark ? "#6ee7b7" : "#047857",
            selfHeaderColor: isDark ? "#fcd34d" : "#b45309",
            rowBorder: isDark ? "#1f2937" : "#e5e7eb",
            levelBadgeBg: isDark ? "rgba(5, 150, 105, 0.25)" : "#d1fae5",
            levelBadgeColor: isDark ? "#6ee7b7" : "#047857",
            commissionColor: isDark ? "#4ade80" : "#15803d",
        }),
        [isDark, styles.card]
    );

    const hasData = downline.length > 0 || selfPlans.length > 0;

    const fetchChart = useCallback(
        async (isRefresh = false) => {
            const token = localStorage.getItem("a5srf4wer7w1cd");
            if (!token) {
                setError("Please login again");
                setLoading(false);
                return;
            }

            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            setError(null);

            try {
                const response = await axios.post(
                    `${BASE_API_URL}/recovery-trade/commission-chart`,
                    { token }
                );
                const payload = response?.data;
                const apiError = getApiError(payload);

                if (apiError === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }

                if (apiError) {
                    setError(apiError);
                    toast.error(apiError);
                    return;
                }

                const { downline: rows, selfPlans: plans } = parseChartPayload(
                    payload?.data
                );
                setDownline(rows);
                setSelfPlans(plans);
            } catch (err) {
                const raw =
                    err.response?.data?.message || err.response?.data?.error;
                const errText =
                    typeof raw === "string"
                        ? raw
                        : "Failed to load commission chart";
                setError(errText);
                toast.error(errText);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [navigate]
    );

    useEffect(() => {
        fetchChart();
    }, [fetchChart]);

    const handleRefresh = () => fetchChart(true);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                className="d-flex flex-column gap-3"
                style={{ maxWidth: 480, margin: "0 auto", paddingBottom: "24px" }}
            >
                <motion.div variants={MOTION.item} style={styles.hero(HERO_GRADIENT)}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-1">
                                <MdShowChart size={22} />
                                Commission chart
                            </h5>
                            <p className="mb-0 small opacity-75">
                                Self commission & downline levels
                            </p>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-flex align-items-center"
                            style={{
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.3)",
                                color: "#fff",
                                borderRadius: "10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            aria-label="Refresh chart"
                        >
                            <motion.span
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{
                                    duration: 0.8,
                                    repeat: refreshing ? Infinity : 0,
                                    ease: "linear",
                                }}
                            >
                                <MdRefresh size={20} />
                            </motion.span>
                        </motion.button>
                    </div>
                </motion.div>

                {error && !loading && (
                    <motion.div variants={MOTION.item}>
                        <div className="p-3 text-center" style={styles.card}>
                            <p className="mb-3" style={{ color: styles.textMuted }}>
                                {error}
                            </p>
                            <motion.button
                                type="button"
                                className="btn"
                                style={{
                                    ...styles.submitBtn,
                                    background:
                                        "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                    boxShadow: "0 6px 20px rgba(5, 150, 105, 0.35)",
                                }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleRefresh}
                            >
                                Try again
                            </motion.button>
                        </div>
                    </motion.div>
                )}

                {loading && (
                    <motion.div variants={MOTION.item} className="text-center py-5">
                        <div
                            className="spinner-border text-success mb-2"
                            role="status"
                        />
                        <p className="mb-0 small" style={{ color: styles.textMuted }}>
                            Loading commission chart…
                        </p>
                    </motion.div>
                )}

                {!loading && !error && selfPlans.length > 0 && (
                    <>
                        <p className="mb-0 px-1" style={styles.sectionTitle}>
                            Self commission
                        </p>
                        <div style={tableStyles.card}>
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr
                                            style={{
                                                backgroundColor: tableStyles.headerBg,
                                            }}
                                        >
                                            <th
                                                className="ps-3 py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.selfHeaderColor,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                Plan
                                            </th>
                                            <th
                                                className="py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.selfHeaderColor,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    Range
                                                </span>
                                            </th>
                                            <th
                                                className="py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.selfHeaderColor,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    Recovery
                                                </span>
                                            </th>
                                            <th
                                                className="pe-3 py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.selfHeaderColor,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    Max
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selfPlans.map((plan, index) => (
                                            <tr
                                                key={`${plan.plan}-${index}`}
                                                style={{
                                                    borderBottom: `1px solid ${tableStyles.rowBorder}`,
                                                }}
                                            >
                                                <td className="ps-3 py-3">
                                                    <PlanBadge plan={plan.plan} />
                                                </td>
                                                <td
                                                    className="py-3 fw-semibold"
                                                    style={{ fontSize: "0.82rem" }}
                                                >
                                                    {formatAmount(plan.from)} –{" "}
                                                    {formatAmount(plan.to)}
                                                </td>
                                                <td className="py-3 fw-semibold">
                                                    {formatRecoveryMultiplier(plan.recovery)}
                                                </td>
                                                <td className="pe-3 py-3 fw-semibold">
                                                    {plan.max_month != null
                                                        ? `${plan.max_month} M`
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {!loading && !error && downline.length > 0 && (
                    <>
                        <p className="mb-0 px-1" style={styles.sectionTitle}>
                            Downline commission
                        </p>
                        <div style={tableStyles.card}>
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr
                                            style={{
                                                backgroundColor: tableStyles.headerBg,
                                            }}
                                        >
                                            <th
                                                className="ps-3 py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.downlineHeaderColor,
                                                    whiteSpace: "nowrap",
                                                    width: "56px",
                                                }}
                                            >
                                                Level
                                            </th>

                                            <th
                                                className="pe-3 py-3 fw-bold text-end"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.downlineHeaderColor,
                                                    whiteSpace: "nowrap",
                                                    width: "72px",
                                                }}
                                            >
                                                <span className="d-inline-flex align-items-center justify-content-end gap-1">
                                                    Comm
                                                </span>
                                            </th>


                                            <th
                                                className="py-3 fw-bold"
                                                style={{
                                                    fontSize: "0.72rem",
                                                    color: tableStyles.downlineHeaderColor,
                                                }}
                                            >
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    Rule
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {downline.map((row) => (
                                            <tr
                                                key={row.level}
                                                style={{
                                                    borderBottom: `1px solid ${tableStyles.rowBorder}`,
                                                }}
                                            >
                                                <td className="ps-3 py-3 align-top">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={{
                                                            fontSize: "0.75rem",
                                                            fontWeight: 700,
                                                            backgroundColor:
                                                                tableStyles.levelBadgeBg,
                                                            color: tableStyles.levelBadgeColor,
                                                        }}
                                                    >
                                                        L{row.level}
                                                    </span>
                                                </td>

                                                <td className="pe-3 py-3 align-top text-end">
                                                    <span
                                                        className="fw-bold"
                                                        style={{
                                                            fontSize: "0.9rem",
                                                            color: tableStyles.commissionColor,
                                                        }}
                                                    >
                                                        {formatCommission(row.commission)}
                                                    </span>
                                                </td>

                                                <td
                                                    className="py-3 align-top"
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        lineHeight: 1.45,
                                                        color: isDark
                                                            ? "#e5e7eb"
                                                            : "#374151",
                                                    }}
                                                >
                                                    {formatDownlineConditions(row)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {!loading && !error && !hasData && (
                    <motion.div variants={MOTION.item} style={styles.empty}>
                        <MdShowChart size={40} className="mb-2 opacity-50" />
                        <p className="mb-0 fw-semibold">No chart data available</p>
                    </motion.div>
                )}

                {!loading && !error && hasData && (
                    <motion.div variants={MOTION.item}>
                        <div
                            style={{
                                ...styles.notice,
                                backgroundColor: isDark
                                    ? "rgba(5, 150, 105, 0.12)"
                                    : "#ecfdf5",
                                border: `1px solid ${isDark ? "rgba(52, 211, 153, 0.35)" : "#a7f3d0"}`,
                                color: isDark ? "#6ee7b7" : "#047857",
                            }}
                        >
                            <strong>Self commission</strong>
                            <p className="mb-2 mt-2 small">
                                Recovery is shown as a multiplier (e.g. 2x) on your
                                plan tier by investment range.
                            </p>
                            <strong>Downline commission</strong>
                            <p className="mb-0 mt-2 small">
                                Each level lists what you need to qualify and the
                                commission percentage you earn from that level.
                            </p>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default RecoveryTradeCommissionChart;
